import { useEffect, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { cleanText, isCleanText } from '@/utils/sanitize';

interface AddContentItemModalProps {
  isOpen: boolean;
  /**
   * Parent performs the API call + refresh. Throw to keep the modal open.
   * Only a title is collected — the content type is derived from the course,
   * and HTML items are seeded with the course's design theme server-side.
   */
  onSave: (data: { title: string }) => Promise<void>;
  onClose: () => void;
}

export default function AddContentItemModal({ isOpen, onSave, onClose }: AddContentItemModalProps) {
  const [title, setTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setError('');
      setIsSaving(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const cleaned = cleanText(title);
    if (cleaned.trim().length < 3) {
      setError('Title must be at least 3 characters');
      return;
    }
    if (!isCleanText(cleaned)) {
      setError('Please use only letters, numbers and standard punctuation');
      return;
    }
    setError('');
    setIsSaving(true);
    try {
      await onSave({ title: cleaned });
    } catch {
      // Parent surfaces the error toast; keep the modal open.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-3">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand-blue-600 flex items-center justify-center">
              <Plus className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Add Content Item</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => { setError(''); setTitle(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !isSaving) handleSave(); }}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 text-sm"
            placeholder="e.g., Introduction"
            autoFocus
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          <p className="text-xs text-gray-400 mt-2">
            The content type and design theme are taken from the course automatically.
          </p>
        </div>

        {/* Footer */}
        <div className="border-t px-5 py-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-5 py-2 text-sm bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 transition-colors disabled:opacity-50 font-medium flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Adding...</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Add Content Item</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
